import {
  buildThemeFromServerTheme,
  BuyBoxDialog,
  CustomThemeProvider,
  DEFAULT_THEME_OPTIONS,
  getRootFontSize,
  useUpdateCustomTheme,
} from "@brandclub/common-ui";
import { styled, StyledEngineProvider, ThemeOptions } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { produce } from "immer";
import _isEqual from "lodash/isEqual";
import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
} from "react";
import { CookiesProvider } from "react-cookie";
import { createPortal } from "react-dom";
import { appThemeQueryOptions } from "./api/cache/appThemeQueryOptions";
import { themeSetupQueryOptions } from "./api/cache/themeSetupQueryOptions";
import ShareProductDialog from "./components/dialog/ShareDialog";
import { useUserProfile } from "./hooks/useUserProfile";
import InpageAddToBagButton from "./modules/AddToBagButton/InpageAddToBagButton";
import BCBagButton from "./modules/BagButton/BCBagButton";
import BCCheckoutBar, {
  BCCheckoutBarProps,
} from "./modules/CheckoutBar/BCCheckoutBar";
import GetAppBanners from "./modules/GetAppBanners";
import MembershipButton from "./modules/MembershipButton/MembershipButton";
import BCProfileButton from "./modules/ProfileButton/BCProfileButton";

import PurchaseRewardDialog from "./components/dialog/PurchaseRewardDialog";
// import { AutoShowMembershipDialog } from "./components/dialog/MembershipDialog/AutoShowMembershipDialog";
const ThemeLiveSetup = lazy(
  () => import("./components/theme-setup/LiveThemeSetup")
);

const FontFamilyWrapper = styled("div")(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
}));

const InAppCheckout = () => {
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useUserProfile();
  const isLoggedIn = !isUserProfileLoading && !!userProfile;
  const bagButtonElement = document.querySelector(".bc-header-cart-icon");
  const bagButtonElementMobile = document.querySelector(
    ".bc-header-cart-icon-mobile"
  );

  const membershipButtonElement = document.querySelector(
    ".bc-header-membership-icon"
  ) as HTMLDivElement | null;

  const membershipButtonElementMobile = document.querySelector(
    ".bc-header-membership-icon-mobile"
  ) as HTMLDivElement | null;

  const profileButtonElement = document.querySelector(
    ".bc-header-profile-icon"
  ) as HTMLDivElement | null;

  const profileButtonElementMobile = document.querySelector(
    ".bc-header-profile-icon-mobile"
  ) as HTMLDivElement | null;

  const inpageAddToBagButtonElement = document.querySelector(
    ".bc-inpage-addtobag-button"
  ) as HTMLDivElement | null; //<div class="bc-inpage-addtobag-button"></div> in main-product.liquid

  const inpageAddToBagButtonElementMobile = document.querySelector(
    ".bc-inpage-addtobag-button-mobile"
  ) as HTMLDivElement | null;

  useEffect(() => {
    // Hide membership elements when user is logged in to preserve layout
    if (membershipButtonElementMobile) {
      if (isLoggedIn) {
        membershipButtonElementMobile.style.display = "none";
      } else {
        membershipButtonElementMobile.style.removeProperty("display");
      }
    }

    if (membershipButtonElement) {
      if (isLoggedIn) {
        membershipButtonElement.style.display = "none";
      } else {
        membershipButtonElement.style.removeProperty("display");
      }
    }
  }, [isLoggedIn]);

  return (
    <>
      {bagButtonElement
        ? createPortal(<BCBagButton />, bagButtonElement)
        : null}

      {bagButtonElementMobile
        ? createPortal(<BCBagButton mobile={true} />, bagButtonElementMobile)
        : null}

      {membershipButtonElement
        ? createPortal(<MembershipButton />, membershipButtonElement)
        : null}

      {membershipButtonElementMobile
        ? createPortal(
            <MembershipButton mobile={true} />,
            membershipButtonElementMobile
          )
        : null}

      {profileButtonElement
        ? createPortal(<BCProfileButton />, profileButtonElement)
        : null}

      {profileButtonElementMobile
        ? createPortal(
            <BCProfileButton mobile={true} />,
            profileButtonElementMobile
          )
        : null}
      {inpageAddToBagButtonElement
        ? createPortal(<InpageAddToBagButton />, inpageAddToBagButtonElement)
        : null}

      {inpageAddToBagButtonElementMobile
        ? createPortal(
            <InpageAddToBagButton mobile={true} />,
            inpageAddToBagButtonElementMobile
          )
        : null}
    </>
  );
};

const ThemeInitializer = (props: PropsWithChildren) => {
  const [prevThemeOptions, setThemeOptions] = useUpdateCustomTheme();
  const { data: styleData } = useQuery(appThemeQueryOptions());

  useEffect(() => {
    const parsedTheme = buildThemeFromServerTheme(styleData);
    if (parsedTheme && !_isEqual(parsedTheme, prevThemeOptions)) {
      setThemeOptions(parsedTheme);
    }
  }, [styleData]);

  useEffect(() => {
    const link = document.createElement("link");
    if (styleData?.settings?.fonts) {
      if (styleData.settings.fonts.fontFamilyLink) {
        // download the font from the link and apply so it's available for the theme
        link.rel = "stylesheet";
        link.href = styleData.settings.fonts.fontFamilyLink;
        document.head.appendChild(link);
      }
    }
  }, [styleData]);

  return <FontFamilyWrapper>{props.children}</FontFamilyWrapper>;
};

const CheckoutComponent = () => {
  const [customTheme] = useUpdateCustomTheme();
  // Default to inpage checkout when custom theme data is missing to prevent checkout bar flashing during load
  const activeCheckoutBar = customTheme?.dtcMode === "checkout-bar";
  if (!customTheme?.content) {
    // if the custom theme content is not loaded, we don't want to render the checkout bar or inapp checkout to prevent flashing during load
    return null;
  }
  return activeCheckoutBar ? <BCCheckoutBar /> : <InAppCheckout />;
};

const CheckoutShopApp: React.FC<BCCheckoutBarProps> = () => {
  const { data: themeSetupResponse } = useQuery(themeSetupQueryOptions());
  const themeOptions = useMemo(() => {
    return produce(DEFAULT_THEME_OPTIONS as ThemeOptions, (draft) => {
      if (typeof draft.typography === "function") {
        return;
      }
      draft.typography ??= {};
      // this is required for sites that modify the root font size for their own rem calculations. we must ensure that the theme is using the same root font size as the site to avoid incorrect font sizes.
      draft.typography.htmlFontSize = getRootFontSize();
      return;
    });
  }, []);
  return (
    <CookiesProvider>
      <StyledEngineProvider injectFirst>
        <CustomThemeProvider themeOptions={themeOptions}>
          <ThemeInitializer>
            <Suspense fallback={null}>
              {!!themeSetupResponse?.setupId && <ThemeLiveSetup />}
            </Suspense>
            <BuyBoxDialog />
            <PurchaseRewardDialog />
            {/* <AutoShowMembershipDialog /> */}
            <ShareProductDialog />
            <GetAppBanners />
            <CheckoutComponent />
          </ThemeInitializer>
        </CustomThemeProvider>
      </StyledEngineProvider>
    </CookiesProvider>
  );
};

export default CheckoutShopApp;
